import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import T from "prop-types";
import styled from "styled-components/macro";
import { debounce } from "lodash";
import Placeholder from "@tiptap/extension-placeholder";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import BubbleMenu from "./BubbleMenu";
import Text from "@tiptap/extension-text";
import History from "@tiptap/extension-history";
import { NewParagraphOnShiftEnter } from "./extensions/NewPragraphOnShiftEnter";

const EditorContentStyled = styled(EditorContent)`
  width: 100%;

  .ProseMirror {
    background-color: var(--neutral-200);
    border: 1px solid var(--neutral-180);
    border-radius: var(--border-radius-1);
    width: 100%;

    outline: none;
    min-height: 96px;
    padding: 9px 12px;
    font-family: "Roboto";
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: var(--neutral-100);

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: var(--neutral-160);
      float: left;
      height: 0;
      pointer-events: none;
    }

    &:hover {
      border: 1px solid var(--neutral-170);
    }

    &:focus-within,
    &:focus-visible {
      box-shadow: 0px 0px 0px 3px rgba(255, 214, 219, 0.5);
      border: 1px solid var(--primary-140);
    }
  }
`;

const Tiptap = ({ content, defaultContent = "", onChange, placeholder, handleKeyDown, dependecies = [] }) => {
  const debouncedOnChange = useCallback(
    debounce(({ editor }) => {
      const html = editor.isEmpty ? defaultContent : editor.getHTML();
      onChange(html);
    }, 1000),
    [onChange]
  );

  const editor = useEditor(
    {
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Underline,
        History,
        Placeholder.configure({
          placeholder: placeholder,
        }),
        NewParagraphOnShiftEnter,
      ],
      content: content,
      onUpdate: debouncedOnChange,
      onBlur: () => debouncedOnChange.flush(),
      editorProps: {
        handleDOMEvents: {
          keydown: (_, event) => {
            if (handleKeyDown) handleKeyDown(event, () => debouncedOnChange.flush());
          },
        },
      },
    },
    [placeholder, ...dependecies]
  );

  return (
    <>
      {editor && (
        <div>
          <BubbleMenu editor={editor} />
        </div>
      )}
      <EditorContentStyled editor={editor} />
    </>
  );
};

Tiptap.propTypes = {
  content: T.string,
  defaultContent: T.string,
  onChange: T.func,
  placeholder: T.string,
  handleKeyDown: T.func,
  dependecies: T.array,
};

export default Tiptap;
